import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { DatePicker, TimePicker } from '../DateTimePicker';
import { getAllUsersDropdown } from '../../services/api';

function getPerthToday() {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Perth' }));
  return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
}

export function BookScheduleModal({ isOpen, onClose, onSubmit, loading, descTitle }) {
  const [formData, setFormData] = useState({
    start_date: '', end_date: '', remarks: '',
    assignee_id: '', start_time: '', end_time: ''
  });
  const { data: users } = useQuery({
    queryKey: ['usersDropdown'], queryFn: () => getAllUsersDropdown().then(r => r.data), enabled: isOpen
  });
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="book-schedule-modal">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-4 border-b"><h2 className="font-semibold">Book a schedule</h2></div>
        <form onSubmit={(e) => { e.preventDefault(); if (!formData.start_date || !formData.end_date || !formData.assignee_id) { toast.error('Please fill all required fields'); return; } onSubmit(formData); }} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm text-slate-500 mb-1">Start Date</label><DatePicker value={formData.start_date} onChange={v => setFormData({...formData, start_date: v})} testId="book-start-date" /></div>
            <div><label className="block text-sm text-slate-500 mb-1">End Date</label><DatePicker value={formData.end_date} onChange={v => setFormData({...formData, end_date: v})} testId="book-end-date" /></div>
          </div>
          <div>
            <label className="block text-sm text-slate-500 mb-1">Assignee</label>
            <select value={formData.assignee_id} onChange={e => setFormData({...formData, assignee_id: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" data-testid="book-assignee">
              <option value="">Select Assignee</option>
              {users?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {formData.assignee_id && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm text-slate-500 mb-1">Start Time</label><TimePicker value={formData.start_time} onChange={v => setFormData({...formData, start_time: v})} /></div>
              <div><label className="block text-sm text-slate-500 mb-1">End Time</label><TimePicker value={formData.end_time} onChange={v => setFormData({...formData, end_time: v})} /></div>
            </div>
          )}
          <div><label className="block text-sm text-slate-500 mb-1">Remarks</label><textarea value={formData.remarks} onChange={e => setFormData({...formData, remarks: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" rows={2} /></div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-500 text-white rounded flex items-center gap-2" data-testid="book-submit">{loading && <Loader2 size={16} className="animate-spin" />}Book</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// === CONFIRM SET ONGOING MODAL ===


export function SetOngoingModal({ isOpen, onClose, onSubmit, loading, descTitle }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="set-ongoing-modal">
      <div className="bg-white rounded-lg w-full max-w-sm p-6 text-center">
        <div className="text-blue-500 text-5xl mb-4">?</div>
        <p className="text-sm text-slate-600 mb-2">Are you sure you want to change <b className="uppercase">{descTitle}</b> status to <b>ON-GOING?</b></p>
        <p className="text-sm mb-4">Click confirm to continue</p>
        <div className="flex justify-center gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded">Cancel</button>
          <button onClick={onSubmit} disabled={loading} className="px-4 py-2 text-sm bg-blue-500 text-white rounded flex items-center gap-2" data-testid="confirm-ongoing">{loading && <Loader2 size={16} className="animate-spin" />}Confirm</button>
        </div>
      </div>
    </div>
  );
}

// === MARK COMPLETED MODAL (with date/time) ===


export function SetCompletedModal({ isOpen, onClose, onSubmit, loading, descTitle }) {
  const [completionDate, setCompletionDate] = useState(getPerthToday());
  const [completionTime, setCompletionTime] = useState(() => {
    return new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Australia/Perth' });
  });
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="set-completed-modal">
      <div className="bg-white rounded-lg w-full max-w-sm p-6">
        <div className="text-center mb-4">
          <div className="text-blue-500 text-5xl mb-2">?</div>
          <p className="text-sm text-slate-600">Are you sure you want to change <b className="uppercase">{descTitle}</b> status to <b>COMPLETED?</b></p>
          <p className="text-sm">Click confirm to continue</p>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className="block text-sm text-slate-500 mb-1">Completion Date</label><DatePicker value={completionDate} onChange={v => setCompletionDate(v)} testId="completion-date" /></div>
          <div><label className="block text-sm text-slate-500 mb-1">Completion Time</label><TimePicker value={completionTime} onChange={v => setCompletionTime(v)} testId="completion-time" /></div>
        </div>
        <div className="flex justify-center gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded">Cancel</button>
          <button onClick={() => { if (!completionDate) { toast.error('Please fill in completion date'); return; } onSubmit(completionDate, completionTime); }} disabled={loading} className="px-4 py-2 text-sm bg-blue-500 text-white rounded flex items-center gap-2" data-testid="confirm-completed">{loading && <Loader2 size={16} className="animate-spin" />}Confirm</button>
        </div>
      </div>
    </div>
  );
}

// === EDIT DESCRIPTION MODAL ===
